import {Button, GenericSelect, Icon, TextRegular} from '@hm/fundamental'
import {mdiDelete} from '@mdi/js'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import type {LimsMeasurementType} from '../../types/LimsMeasurementType'
import type {Unit} from '../../types/Unit'
import {getLimsMeasurementTypeLabel} from '../../utils'

export interface UnsavedRow {
  tempId: string
}

interface UnsavedMappingRowProps {
  index: number
  isDisabled: boolean
  availableLimsTypes: LimsMeasurementType[]
  units: Unit[]
  onLimsTypeChange: (limsTypeId: number) => void
  onDelete: () => void
  testIdPrefix: string
}

export const UnsavedMappingRow: React.FC<UnsavedMappingRowProps> = ({
  index,
  isDisabled,
  availableLimsTypes,
  units,
  onLimsTypeChange,
  onDelete,
  testIdPrefix
}) => {
  const {t} = useTranslation()
  const [selectedLimsTypeId, setSelectedLimsTypeId] = useState<number | ''>('')

  return (
    <div
      className="flex items-center gap-4 max-md:items-start"
      data-test-id={`${testIdPrefix}_row_${index}`}
    >
      <div className="h-8 w-8 rounded-full border border-gray-300 flex items-center justify-center">
        <TextRegular className="text-sm text-foreground_faded">{index + 1}</TextRegular>
      </div>

      <div className="flex items-center gap-4 max-md:flex-col max-md:items-start">
        <div className="min-w-[250px] max-md:min-w-0">
          <GenericSelect
            name={`${testIdPrefix}_row_${index}_lims_select`}
            value={selectedLimsTypeId}
            label={t('measurementsSettings.limsMeasurementType')}
            isDisabled={isDisabled}
            className="!w-64"
            values={availableLimsTypes.map((limsType) => ({
              value: limsType.id,
              label: getLimsMeasurementTypeLabel(limsType)
            }))}
            setValue={(value) => {
              const nextValue = value === undefined || value === '' ? '' : Number(value)

              setSelectedLimsTypeId(nextValue)

              if (value !== undefined && value !== '') {
                onLimsTypeChange(Number(value))
              }
            }}
            data-test-id={`${testIdPrefix}_row_${index}_lims_select`}
          />
        </div>

        {units.length === 0 ? (
          <TextRegular
            className="w-28 text-sm text-foreground_faded max-md:w-64"
            data-test-id={`${testIdPrefix}_row_${index}_no_unit`}
          >
            {t('measurementsSettings.noUnit')}
          </TextRegular>
        ) : (
          <div className="w-28 shrink-0 max-md:w-64">
            <GenericSelect
              name={`${testIdPrefix}_row_${index}_unit_select`}
              value=""
              label={t('measurementsSettings.unit')}
              isDisabled={isDisabled || selectedLimsTypeId === ''}
              className="!w-28 max-md:!w-64"
              values={[
                {value: '', label: t('measurementsSettings.noUnit')},
                ...units.map((unit) => ({value: unit.id, label: unit.name}))
              ]}
              setValue={() => undefined}
              data-test-id={`${testIdPrefix}_row_${index}_unit_select`}
            />
          </div>
        )}
      </div>

      <Button
        variant="tertiary"
        colorClass="text-negative"
        disabled={isDisabled}
        onClick={onDelete}
        data-test-id={`${testIdPrefix}_row_${index}_delete`}
      >
        <Icon path={mdiDelete} />
      </Button>
    </div>
  )
}
